"use strict";

/**
 * Clase `Generality` para gestionar funcionalidades generales relacionadas con formularios.
 * Maneja interacciones comunes como deshabilitar botones, reenviar solicitudes, y configuración de selects.
 */
export class Generality {
    /**
     * Crea una instancia de la clase Generality.
     * @param {Object} notification - Objeto para manejar notificaciones.
     * @param {Object} httpError - Objeto para manejar errores HTTP.
     * @param {Object} tool - Objeto para métodos auxiliares
     */
    constructor(notification, httpError, tool) {
        this.notification = notification;
        this.httpError = httpError;
        this.tool = tool;
    }

    /**
     * Habilita/deshabilita los pagos de solicitudes
     */
    changeSetting() {
        if (!this.tool.areElementsPresent(["#changeSetting"])) return;

        const button = document.querySelector("#changeSetting");

        button.addEventListener("click", (e) => {
            const question = this.notification.showSwalConfirm(
                "¡No!",
                "#E3051B",
                "Sí, procede",
                "<p class='m-0 text-center'>Esta acción es reversible.</p>",
                "warning",
                "<p class='m-0 text-center'>¿Realmente desea continuar con esta acción?</p>"
            );

            question.then((result) => {
                if (result.value) {
                    axios({
                        method: "put",
                        url: `/admin/setting/change`,
                    })
                        .then((response) => {
                            this.notification.showToasted(
                                "alive",
                                "success",
                                `<p class='p-0'>${response.data}</p>`
                            );
                            setTimeout(() => (location.href = `/`), 1500);
                        })
                        .catch((error) => {
                            this.httpError.handleHttpError(error);
                        });
                } else {
                    this.notification.showToasted(
                        "alive",
                        "error",
                        "<p class='m-0 text-center'>El sistema de pagos no ha sido afectado.</p>"
                    );
                }
            });
        });
    }

    /**
     * Deshabilita temporalmente los botones de formularios al enviar.
     * Aplica a todos los formularios con clase `.form-main` y botones con clase `.btn-form`.
     */
    disableForm() {
        document.querySelectorAll(".form-main").forEach((form) => {
            form.addEventListener("submit", () => {
                if (!form.querySelector('input[name="g-recaptcha-response"]')) {
                    return;
                }

                const buttons = document.querySelectorAll(".btn-form");

                buttons.forEach((button) => {
                    button.disabled = true;
                    button.classList.add("cursor-not-allowed");

                    setTimeout(() => {
                        button.disabled = false;
                        button.classList.remove("cursor-not-allowed");
                    }, 60000); // Rehabilita después de 60 segundos
                });
            });
        });
    }

    /**
     * Muestra un mensaje cuando se ingresa el número de identificación en el formulario de PQRS
     */
    enterNumberIdentification() {
        if (!this.tool.areElementsPresent(["#formPqrs"])) return;

        const form = document.querySelector("#formPqrs");
        const identification = document.querySelector("#identification");

        if (!form) return;

        identification.addEventListener("change", () => {
            let identificationValue = identification.value;

            axios({
                data: { identificationValue },
                method: "put",
                url: `/complaints/search/${identificationValue}`,
            })
                .then((response) => {
                    if (!response.data) {
                        document
                            .querySelector("#textAlert")
                            .classList.add("hidden");
                        return;
                    }

                    let alert = document.querySelector("#textAlert");

                    alert.innerHTML = `${response.data.message}<a href="${response.data.login_url}" class="font-bold underline" target="_blank">Iniciar sesión</a>`;

                    alert.classList.remove("hidden");
                })
                .catch((error) => {
                    this.httpError.handleHttpError(error);
                });
        });
    }

    /**
     * Muestra y gestiona el botón que desplaza la página hasta la parte superior.
     */
    goUp() {
        if (!this.tool.areElementsPresent(["#buttonTop"])) return;

        const buttonTop = document.querySelector("#buttonTop");

        window.onscroll = () => {
            const scrollTop =
                document.documentElement.scrollTop || document.body.scrollTop;
            buttonTop.classList.toggle("hidden", scrollTop <= 20);
        };

        buttonTop.addEventListener("click", () => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        });
    }

    /**
     * Inicializa reCAPTCHA v3 en el formulario de PQRS
     */
    initRecaptcha() {
        if (!this.tool.areElementsPresent(["#formPqrs"])) return;

        if (typeof grecaptcha === "undefined") {
            console.error("reCAPTCHA no está cargado");
            return;
        }

        const form = document.querySelector("#formPqrs");

        if (!form) return;

        grecaptcha.ready(() => {
            form.addEventListener("submit", (e) => {
                e.preventDefault();

                const button = form.querySelector('button[type="submit"]');
                if (button) button.disabled = true;

                grecaptcha
                    .execute(window.RECAPTCHA_SITE_KEY, { action: "submit" })
                    .then((token) => {
                        let input = form.querySelector(
                            'input[name="g-recaptcha-response"]'
                        );

                        if (!input) {
                            input = document.createElement("input");
                            input.type = "hidden";
                            input.name = "g-recaptcha-response";
                            form.appendChild(input);
                        }

                        input.value = token;
                        form.submit();
                    })
                    .catch(() => {
                        if (button) button.disabled = false;
                        alert("No se pudo validar el reCAPTCHA.");
                    });
            });
        });
    }

    /**
     * Maneja el evento de reenvío de solicitudes.
     */
    resendApplication() {
        document.querySelectorAll(".resendApplication").forEach((item) => {
            item.addEventListener("click", () => {
                const applicationId = item.dataset.id;
                const isGraduate = item.dataset.isGraduate === "1";

                const additionalMessage = !isGraduate
                    ? `<p class='mt-2 mb-0 text-justify'>
                        <span class='font-bold'>Importante:</span>
                        Recuerde cargar los documentos requeridos dentro del plazo establecido después de la ceremonia de grado. Si no lo hace, su solicitud será rechazada y no se realizará el reembolso del dinero pagado.
                        </p>`
                    : "";

                const question = this.notification.showSwalConfirm(
                    "¡No!",
                    "#16A34B",
                    "Sí, procede",
                    `<p class='m-0 text-justify'>Recuerde revisar que su información este completa y sea la correcta.</p> ${additionalMessage}`,
                    "success",
                    "<p class='m-0 text-center'>¿Realmente desea reenviar esta solicitud?</p>"
                );

                question.then((result) => {
                    if (result.value) {
                        axios({
                            data: JSON.stringify({ applicationId }),
                            method: "put",
                            url: `/applications/resend/${applicationId}`,
                        })
                            .then((response) => {
                                this.notification.showToasted(
                                    "alive",
                                    "success",
                                    `<p class='p-0'>${response.data}</p>`
                                );
                                setTimeout(
                                    () =>
                                        (location.href = `/applications/${applicationId}`),
                                    1500
                                );
                            })
                            .catch((error) => {
                                this.httpError.handleHttpError(error);
                            });
                    } else {
                        this.notification.showToasted(
                            "alive",
                            "error",
                            "<p class='m-0 text-center'>La solicitud no ha sido enviada.</p>"
                        );
                    }
                });
            });
        });
    }

    /**
     * Inicializa los elementos `<select>` con clase `.selectr` usando la librería Selectr.
     * @throws Error si ocurre un problema al inicializar Selectr.
     */
    selectConfig() {
        document.querySelectorAll(".selectr").forEach((item) => {
            const attributeId = item.getAttribute("id");
            const element = document.getElementById(attributeId);

            this.tool.initSelectr(element);
        });
    }

    /**
     * Maneja el evento de envío de solicitudes.
     */
    sendApplication() {
        document.querySelectorAll(".sendApplication").forEach((item) => {
            item.addEventListener("click", () => {
                const applicationId = item.dataset.id;
                const isGraduate = item.dataset.isGraduate === "1";

                const url =
                    "/storage/doc/condiciones_para_la_eleccion_del_tramite_de_la_matricula_profesional.pdf";

                const additionalMessage = !isGraduate
                    ? `<p class='mt-2 mb-0 text-justify'>
                        <span class='font-bold'>Importante:</span>
                        Recuerde cargar los documentos requeridos dentro del plazo establecido después de la ceremonia de grado. Si no lo hace, su solicitud será rechazada y no se realizará el reembolso del dinero pagado.
                        </p>`
                    : "";

                const question = this.notification.showSwalConfirm(
                    "¡No!",
                    "#16A34B",
                    "Sí, procede",
                    `<p class='m-0 text-justify'><span class='mt-1 font-bold'>Una vez radicada la solicitud no se puede cambiar el tipo de trámite</span>, recuerde revisar que su información este completa y correcta, en caso de encontrar algún dato que no corresponda al tipo de trámite de la solicitud, esta se resolverá rechazandola, por favor revise las siguientes indicaciones para mayor información antes de radicar su solicitud: <a class='text-blue-cpqcol-1000 font-bold' target='_blank' rel='noopener' href='${url}'>Ver indicaciones</a>.</p>
                    ${additionalMessage}`,
                    "success",
                    "<p class='m-0 text-center'>¿Realmente desea radicar esta solicitud?</p>"
                );

                question.then((result) => {
                    if (result.value) {
                        axios({
                            data: JSON.stringify({ applicationId }),
                            method: "put",
                            url: `/applications/send/${applicationId}`,
                        })
                            .then((response) => {
                                this.notification.showToasted(
                                    "alive",
                                    "success",
                                    `<p class='p-0'>${response.data}</p>`
                                );
                                setTimeout(
                                    () =>
                                        (location.href = `/applications/${applicationId}`),
                                    1500
                                );
                            })
                            .catch((error) => {
                                this.httpError.handleHttpError(error);
                            });
                    } else {
                        this.notification.showToasted(
                            "alive",
                            "error",
                            "<p class='m-0 text-center'>La solicitud no ha sido radicada.</p>"
                        );
                    }
                });
            });
        });
    }
}
